import { DatePicker, Select, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type { ComponentProps } from "react";
import { Link } from "react-router-dom";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import {
	NOTIFICATION_STATUS_LABELS,
	NOTIFICATION_STATUS_TONES,
	NOTIFICATION_TYPE_COLORS,
	NOTIFICATION_TYPE_LABELS,
	NOTIFICATION_TYPE_OPTIONS,
	RELATED_ENTITY_LABELS,
} from "../constants";
import { useNotificationList } from "../hooks/useNotifications";
import type { Notification, NotificationStatus, NotificationType } from "../types";

type RangePickerOnChange = NonNullable<ComponentProps<typeof DatePicker.RangePicker>["onChange"]>;

export interface NotificationFilters {
	type?: NotificationType;
	dateFrom?: string;
	dateTo?: string;
}

interface NotificationTableProps {
	page: number;
	limit: number;
	onPageChange: (page: number, pageSize: number) => void;
	filters: NotificationFilters;
	onFiltersChange: (filters: NotificationFilters) => void;
}

/** Route prefixes for related-entity detail pages */
const RELATED_ENTITY_ROUTES: Record<string, string> = {
	task: "/tasks",
	equipment: "/equipment",
	staff: "/staff",
};

function RelatedEntityCell({ record }: { record: Notification }) {
	if (!(record.relatedEntityType && record.relatedEntityId)) {
		return <span>—</span>;
	}
	const label = RELATED_ENTITY_LABELS[record.relatedEntityType] ?? record.relatedEntityType;
	const routePrefix = RELATED_ENTITY_ROUTES[record.relatedEntityType];
	if (routePrefix) {
		return <Link to={`${routePrefix}/${record.relatedEntityId}`}>{label}</Link>;
	}
	return <span>{label}</span>;
}

export function NotificationTable({
	page,
	limit,
	onPageChange,
	filters,
	onFiltersChange,
}: NotificationTableProps) {
	const { data, isLoading } = useNotificationList({ page, limit, ...filters });

	const setFilter = (patch: Partial<NotificationFilters>) => {
		onFiltersChange({ ...filters, ...patch });
	};

	const dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
		filters.dateFrom && filters.dateTo ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)] : null;

	const handleDateRangeChange: RangePickerOnChange = (dates) => {
		if (dates?.[0] && dates?.[1]) {
			setFilter({
				dateFrom: dates[0].startOf("day").toISOString(),
				dateTo: dates[1].endOf("day").toISOString(),
			});
		} else {
			setFilter({ dateFrom: undefined, dateTo: undefined });
		}
	};

	const columns: ColumnsType<Notification> = [
		{
			title: "№",
			key: "index",
			width: 60,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Turi",
			dataIndex: "type",
			key: "type",
			width: 220,
			render: (type: NotificationType) => (
				<Tag color={NOTIFICATION_TYPE_COLORS[type]}>{NOTIFICATION_TYPE_LABELS[type] ?? type}</Tag>
			),
		},
		{
			title: "Sarlavha",
			dataIndex: "title",
			key: "title",
			width: 220,
			ellipsis: true,
		},
		{
			title: "Matn",
			dataIndex: "body",
			key: "body",
			ellipsis: true,
			render: (body: string) => (
				<Typography.Text ellipsis={{ tooltip: body }} style={{ maxWidth: 380 }}>
					{body}
				</Typography.Text>
			),
		},
		{
			title: "Bog'liq obyekt",
			key: "relatedEntity",
			width: 140,
			render: (_: unknown, record: Notification) => <RelatedEntityCell record={record} />,
		},
		{
			title: "Yetkazilish holati",
			dataIndex: "status",
			key: "status",
			width: 160,
			render: (status: NotificationStatus) => (
				<StatusTag tone={NOTIFICATION_STATUS_TONES[status]}>
					{NOTIFICATION_STATUS_LABELS[status] ?? status}
				</StatusTag>
			),
		},
		{
			title: "Sana",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 150,
			render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
		},
	];

	return (
		<div>
			<TableToolbar
				filters={
					<>
						<span className="text-ink-soft">Turi:</span>
						<Select
							placeholder="Barchasi"
							allowClear
							value={filters.type}
							onChange={(type) => setFilter({ type })}
							options={NOTIFICATION_TYPE_OPTIONS}
							style={{ width: 240 }}
						/>
						<span className="text-ink-soft">Sana:</span>
						<DatePicker.RangePicker
							allowClear
							value={dateRange}
							onChange={handleDateRangeChange}
							format="DD.MM.YYYY"
							placeholder={["Boshlanish", "Tugash"]}
							style={{ width: 240 }}
						/>
					</>
				}
			/>
			<Table
				columns={columns}
				dataSource={data?.data?.items}
				loading={isLoading}
				rowKey="id"
				locale={{
					emptyText: <EmptyState description="Bildirishnomalar topilmadi" />,
				}}
				pagination={tablePagination({
					page,
					limit,
					total: data?.data?.meta?.total,
					onChange: onPageChange,
					sizeChanger: false,
				})}
				scroll={{ x: "max-content" }}
				size="middle"
			/>
		</div>
	);
}
